// src/components/TZMMS-INV/TZMMSInv.jsx
// ============================================================================
// TZMMS Inventory Counter — top-level page component
// ----------------------------------------------------------------------------
// PURPOSE
// - Compose the TZMMS inventory counting experience.
// - Keep the render tree easy to scan while the hook owns behavior.
// ============================================================================

import React from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import InventorySummary from './components/InventorySummary.jsx';
import {
  PAGE_MAX_WIDTH,
  PAGE_PADDING_DESKTOP,
  PAGE_PADDING_MOBILE,
  PAGE_ROUTE_PATH,
  PAGE_TITLE,
} from './constants/tzmmsInvUi.js';
import ScannerDialog from './dialogs/ScannerDialog.jsx';
import useTZMMSInvController from './hooks/useTZMMSInvController.js';
import InventoryListPanel from './panels/InventoryListPanel.jsx';

export default function TZMMSInv({ userEmail }) {
  // ============================================================================
  // BLOCK TZI.PAGE.001 — Main controller hook
  // ============================================================================
  const tzi = useTZMMSInvController({ userEmail });

  // ============================================================================
  // BLOCK TZI.PAGE.002 — Derived display flags
  // ----------------------------------------------------------------------------
  // PURPOSE
  // - Keep small display booleans together near the top of the component.
  // - Make button state logic easier to edit later.
  // ============================================================================
  const hasItems = Array.isArray(tzi.entries) && tzi.entries.length > 0;

  // ============================================================================
  // BLOCK TZI.PAGE.003 — JSX-safe handler aliases
  // ----------------------------------------------------------------------------
  // PURPOSE
  // - Satisfy the react/jsx-handler-names lint rule.
  // - Keep render props readable by exposing handler names that begin with
  //   "handle" even when the controller hook uses names like openScanner,
  //   closeToast, or setSelectedEntryId.
  // ============================================================================
  const handleSelectEntry = tzi.setSelectedEntryId;
  const handleNoteChange = tzi.setSessionNote;
  const handleOpenScanner = tzi.openScanner;
  const handleRemoveSelected = tzi.removeSelectedEntry;
  const handleClearAll = tzi.clearAllEntries;
  const handleEmailContents = tzi.emailContents;
  const handleCloseScanner = tzi.closeScanner;
  const handleToggleScannerPaused = tzi.toggleScannerPaused;
  const handleCloseToast = tzi.closeToast;

  // ============================================================================
  // BLOCK TZI.PAGE.004 — Render page
  // ============================================================================
  return (
    <Container
      maxWidth={PAGE_MAX_WIDTH}
      sx={{
        py: { xs: PAGE_PADDING_MOBILE, md: PAGE_PADDING_DESKTOP },
        px: { xs: 1, md: 2 },
      }}
    >
      <Stack spacing={{ xs: 1.5, md: 2.5 }}>
        <Box>
          <Typography variant="h4">{PAGE_TITLE}</Typography>
          <Typography variant="body2" color="text.secondary">
            Route: {PAGE_ROUTE_PATH} — Scan barcodes (QR, Data Matrix, Aztec, EAN-13, Code128),
            review the running list, then email the contents to yourself.
          </Typography>
        </Box>

        {!tzi.resolvedRecipient && (
          <Alert severity="warning">
            This page did not receive a user email, so the &quot;Email Contents&quot; button will be
            blocked until a recipient is available.
          </Alert>
        )}

        <InventorySummary
          count={tzi.entries.length}
          recipient={tzi.resolvedRecipient}
          lastScannedValue={tzi.lastScannedValue}
        />

        {/* ================================================================= */}
        {/* BLOCK TZI.PAGE.004a — Session note field */}
        {/* ================================================================= */}
        <TextField
          label="Session Note"
          placeholder="e.g. Customer name, location, or count notes"
          value={tzi.sessionNote}
          onChange={(event) => handleNoteChange(event.target.value)}
          multiline
          minRows={2}
          maxRows={6}
          fullWidth
          helperText="Appears above the scan list in the emailed report."
        />

        <InventoryListPanel
          entries={tzi.entries}
          selectedEntryId={tzi.selectedEntryId}
          onSelectEntry={handleSelectEntry}
          onOpenScanner={handleOpenScanner}
          onRemoveSelected={handleRemoveSelected}
          onClearAll={handleClearAll}
        />

        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="flex-end">
          <Button
            variant="contained"
            size="large"
            onClick={handleEmailContents}
            disabled={tzi.emailBusy || !hasItems}
            fullWidth
            sx={{ display: { xs: 'flex', md: 'inline-flex' } }}
          >
            {tzi.emailBusy ? 'Sending Email...' : 'Email Contents'}
          </Button>
        </Stack>
      </Stack>

      {/* ===================================================================== */}
      {/* BLOCK TZI.PAGE.005 — Scanner dialog mount */}
      {/* ===================================================================== */}
      <ScannerDialog
        open={tzi.scannerOpen}
        paused={tzi.scannerPaused}
        errorMessage={tzi.scannerError}
        lastScannedValue={tzi.lastScannedValue}
        onClose={handleCloseScanner}
        onTogglePaused={handleToggleScannerPaused}
        onScan={tzi.handleDetectedCodes}
        onError={tzi.handleScannerError}
      />

      {/* ===================================================================== */}
      {/* BLOCK TZI.PAGE.006 — Page-level toast */}
      {/* ===================================================================== */}
      <Snackbar
        open={tzi.toast.open}
        autoHideDuration={3000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseToast} severity={tzi.toast.severity} variant="filled">
          {tzi.toast.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
